/**
 * Auth helpers — replaces the old Supabase client.
 * All data operations now go through the PHP/MySQL API via @/shared/lib/api.
 */

import { api, getAuthToken, clearAuthToken } from "@/shared/lib/api";
import type { AuthUser, TailorProfile } from "@/features/auth/types";
import type { SubscriptionEntitlements } from "@/shared/lib/subscription";

export const isDevBypass = false;

/** Sign out the current user by calling the API and clearing the local token. */
export async function signOut(): Promise<void> {
  try {
    await api.post("auth", "logout");
  } catch {
    // Ignore errors — clear local state regardless
  }
  clearAuthToken();
}

/** Check if a user is currently authenticated (has a valid token). */
export function isAuthenticated(): boolean {
  return !!getAuthToken();
}

/** Fetch the current tailor profile from the API. Returns null if not authenticated. */
export async function fetchCurrentTailor(): Promise<TailorProfile | null> {
  const session = await fetchCurrentSession();
  return session?.tailor ?? null;
}

/** Fetch the current session (tailor profile + team member context). */
export async function fetchCurrentSession(): Promise<{ tailor: TailorProfile; authUser: AuthUser } | null> {
  if (!getAuthToken()) return null;

  try {
    const data = await api.get<{ tailor: TailorProfile; authUser: AuthUser }>("auth", "me");
    return { tailor: data.tailor, authUser: data.authUser ?? null };
  } catch {
    // Token may be expired
    clearAuthToken();
    return null;
  }
}

export async function fetchCurrentEntitlements(): Promise<SubscriptionEntitlements | null> {
  if (!getAuthToken()) return null;
  try {
    return await api.get<SubscriptionEntitlements>("auth", "entitlements");
  } catch {
    return null;
  }
}
